<script lang="ts">
    // "Sign In" form.
    import { onDestroy, onMount } from 'svelte'
    import { writable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import * as yup from 'yup'

    // import { WDATA } from 'hydria/context'
    import { gettext as _, resolve, tngettext, type TGettext } from 'hydria/services/gettext'
    import { lg } from 'hydria/services/language'
    import { PASSWORD_MIN_LENGTH, PASSWORD_SUGGESTIONS } from 'hydria/definitions/passwords'
    import { fade } from 'svelte/transition'

    interface Props {
        token: string
        goto?: (loc: string) => void
    }

    let { token, goto }: Props = $props()

    interface UserRequestData {
        missing: string[]
        done: string[]
        final: boolean
        success: boolean
    }

    // Clear the error after some time
    const CLEAR_ERROR_AFTER_MS = 7500

    // Bunch of functions to call on destroy, like store subscriptions.
    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    const schema = yup.object().shape({
        password: yup.string().required().min(PASSWORD_MIN_LENGTH),
        pconfirm: yup
            .string()
            .required()
            .test({
                name: 'equals',
                exclusive: false,
                message: '',
                test: function (value) {
                    return value === fields.password
                },
            }),
    })

    interface IFields {
        password: string
        pconfirm: string
    }
    const initialFields: IFields = { password: '', pconfirm: '' }
    const fields: IFields = $state(Object.assign({}, initialFields))
    const password = writable(fields.password)
    $effect(() => {
        $password = fields.password
    })

    let emailValidated: boolean = $state(false)

    // When true we are signing in, (e.g. show a spinner)
    const communicating = writable(false)
    const success = writable(false)

    // When not null, there was an error to show.
    // That error is shown for a few seconds or until a value changes.
    const error = writable(null)
    let errorTimer: ReturnType<typeof setTimeout> | null = null

    function clearErrorTimer() {
        if (errorTimer) {
            clearTimeout(errorTimer)
            errorTimer = null
        }
    }
    function clearError() {
        if ($error != null) {
            clearErrorTimer()
            error.set(null)
        }
    }

    unsubs.push(
        error.subscribe((newError) => {
            // This is called when the `error` store changes
            clearErrorTimer()
            if (newError != null) {
                errorTimer = setTimeout(() => {
                    errorTimer = null
                    clearError()
                }, CLEAR_ERROR_AFTER_MS)
            }
        }),
    )

    // Set 'isvalid' variables based on their validity when a value changes
    // @ts-expect-error
    let passwordIsValid = $derived(schema.fields.password.isValidSync(fields.password))
    // @ts-expect-error
    let pconfirmIsValid = $derived(schema.fields.pconfirm.isValidSync(fields.pconfirm))
    let formIsValid = $derived(schema.isValidSync(fields))

    let tokenValid: false | true | null = $state(null)
    let urMode: undefined | 'set-email' | 'set-password' | 'unlock' | 'set-password-after-email' = $state()

    async function validateToken(token: string) {
        tokenValid = null
        if (emailValidated) emailValidated = false

        const headers = new Headers()
        headers.append('Accept', 'application/json')
        headers.append('X-Lg', $lg)

        const r = await fetch(new Request(`/json/session/1/request/${token}`), {
            method: 'GET',
            headers: headers,
        })

        let data: UserRequestData
        try {
            data = await r.json()
        } catch (e: any) {
            console.error('Error parsing response json')
            data = {
                success: false,
                missing: [],
                final: false,
                done: [],
            }
        }

        if (r.status != 200) {
            // throw { success: false, error: data && data.error ? data.error : null }
            tokenValid = false
            return
        }

        if (data.success) {
            // return { success: true }
            tokenValid = true

            if (data.done && data.done.includes('set-email')) {
                console.log('Cond a')
                emailValidated = true
                setTimeout(() => {
                    if (emailValidated) emailValidated = false
                }, 5000)
                if (data.missing && data.missing.includes('set-password')) {
                    urMode = 'set-password'
                } else {
                    urMode = 'set-email'
                    $success = true
                }
            } else if (data.done && data.done.includes('unlock')) {
                urMode = 'unlock'
                $success = true
            } else {
                urMode = 'set-password'
            }
            return
        }
        tokenValid = false
        // throw { success: false, error: data && data.error ? data.error : null }
    }
    onMount(() => {
        validateToken(token)
    })
    $inspect('emailValidated = ', emailValidated)

    // We setup a store so we can easily do stuff when anything changes in the fields.
    // The purpose being to clear the error if there’s one.
    let formData = writable(fields)
    $effect(() => {
        $formData = fields
    })
    unsubs.push(
        formData.subscribe((nv) => {
            if (errorTimer != null) {
                clearError()
            }
        }),
    )

    async function setPassword(token: string, password: string) {
        let fdata = new Blob([JSON.stringify({ new_password: password }, null, 0)], { type: 'application/json' })
        const headers = new Headers()
        headers.append('Accept', 'application/json')
        headers.append('X-Lg', $lg)

        const r = await fetch(new Request(`/json/session/1/request/${token}`), {
            method: 'POST',
            headers: headers,
            body: fdata,
        })

        let data: any
        try {
            data = await r.json()
        } catch {}

        if (r.status != 200) {
            throw { success: false, error: data && data.error ? data.error : null }
        }

        if (data.success) {
            return { success: true }
        }
        throw { success: false, error: data && data.error ? data.error : null }
    }

    async function formSubmit(e: Event) {
        e.preventDefault()

        if (!formIsValid) {
            return
        }
        communicating.set(true)
        let res
        try {
            res = await setPassword(token, $password)
        } catch (e) {
            res = e
        }
        communicating.set(false)
        if (!res.success) {
            if (res.error) error.set(res.error)
            else {
                // @ts-expect-error
                error.set($_('L’opération n’a pas pu être effectuée'))
            }
        } else {
            $success = true
        }
    }

    const suggestions = writable([] as TGettext[])

    // Handle password suggestions
    unsubs.push(
        password.subscribe((pw) => {
            const pwLen = pw.length
            const pwLenDiff = 16 - pwLen
            const sugs: TGettext[] = []

            if (pwLenDiff > 0) {
                sugs.push(tngettext('un caractère à votre mot de passe', '{n} caractères à votre mot de passe', { n: pwLenDiff }))
            }

            for (const sug of PASSWORD_SUGGESTIONS) {
                if (pw.replace(sug.ereg, '').length < 1) {
                    sugs.push(sug.text)
                }
            }

            suggestions.set(sugs)
        }),
    )

    let mainInput: HTMLInputElement | undefined = $state()
    onMount(() => {
        if (mainInput) mainInput.focus()
    })
</script>

<div class="section-title">
    {#if tokenValid === null}
        {$_('Chargement…')}
    {:else if urMode == 'set-email'}
        {$_('Nouvelle adresse courriel')}
    {:else if urMode == 'unlock'}
        {$_('Compte déverrouillé')}
    {:else}
        {$_('Nouveau mot de passe')}
    {/if}
</div>

<form novalidate onsubmit={formSubmit}>
    {#if $success}
        <p class="gen-text one-line">
            {#if urMode == 'set-email'}
                {$_('Votre nouvelle adresse de courriel est maintenant active.')}
            {:else if urMode === 'unlock'}
                {$_('Votre compte est maintenant déverrouillé.')}
            {:else}
                {$_('Votre nouveau mot de passe est maintenant actif.')}
            {/if}
        </p>
        <p>
            <button
                class="btn btn-primary"
                onclick={() => {
                    if (goto) goto('signin')
                    else window.location.replace('/')
                }}>{$_('Retourner à la connexion.')}</button
            >
        </p>
    {:else if tokenValid === null}
        <p class="gen-text one-line">
            {$_('Un moment…')}
        </p>
    {:else if tokenValid === false}
        <p class="gen-text one-line">
            {$_('Le jeton n’est pas ou plus valide.')}
        </p>
        <p>
            <button
                class="btn btn-primary"
                onclick={() => {
                    if (goto) goto('signin')
                    else window.location.replace('/')
                }}>{$_('Retourner à la connexion.')}</button
            >
        </p>
    {:else}
        {#if emailValidated}
            <p class="gen-text one-line" transition:fade>
                {$_('Votre nouvelle adresse de courriel est maintenant active.')}
            </p>
        {/if}
        <div class="inp-password">
            <label for="password" class="sr-only">{$_('Mot de passe')}</label>
            <div class="input-group" class:is-ok={passwordIsValid} class:has-error={!passwordIsValid} class:disabled={$communicating}>
                <span class="input-group-text"><Icon inline icon="mdi:lock" /></span>
                <input class="form-control" id="password" type="password" bind:this={mainInput} bind:value={fields.password} disabled={$communicating} placeholder={$_('Mot de passe')} oninput={() => clearError()} />
            </div>
        </div>

        {#if $password.length < PASSWORD_MIN_LENGTH}
            <div class="password-sugs text-danger one-line">{$_('Nous exigeons au moins {min} caractères.', { min: PASSWORD_MIN_LENGTH })}</div>
        {:else if $suggestions.length}
            <div class="password-sugs">
                <p class="text-warning">{$_('Nous suggérons l’addition de :')}</p>
                <ul>
                    {#each $suggestions as suggestion, i}
                        <li class="text-warning">
                            {$resolve(suggestion)}{#if i == $suggestions.length - 1}.{:else}
                                ;{/if}
                        </li>
                    {/each}
                </ul>
            </div>
        {/if}

        <div class="inp-password">
            <label for="pconfirm" class="sr-only">{$_('Confirmation du mot de passe')}</label>
            <div class="input-group" class:is-ok={pconfirmIsValid || fields.password == ''} class:has-error={!pconfirmIsValid && fields.password != ''} class:disabled={$communicating}>
                <span class="input-group-text"><Icon inline icon="mdi:lock" /></span>
                <input class="form-control" id="pconfirm" type="password" bind:value={fields.pconfirm} disabled={$communicating} placeholder={$_('Confirmation du mot de passe')} oninput={() => clearError()} />
            </div>
        </div>

        {#if $error != null}
            <div class="error">
                <div class="alert alert-danger">{$error}</div>
            </div>
        {:else}
            <div class="inp-action d-grid">
                <button class="btn btn-primary" type="submit" disabled={!formIsValid || $communicating || $error != null}>
                    {#if $communicating}{$_('Veuillez patienter…')}
                    {:else}{$_('Activer le nouveau mot de passe')}
                    {/if}
                </button>
            </div>
        {/if}
    {/if}
</form>
